import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import { motion } from 'framer-motion';
import { brand } from '@/shared/config/brand';
import { useLiveCities } from '@/features/destinations/hooks/useLiveCities';

const phoneScreens = [
  {
    src: '/images/trplin-app-explore.png',
    alt: 'trplin app explore screen',
    rotate: '-6deg',
    offsetY: { xs: 0, md: -20 },
  },
  {
    src: '/images/trplin-app-trips.png',
    alt: 'trplin app my trips screen',
    rotate: '6deg',
    offsetY: { xs: 0, md: 20 },
  },
] as const;

const features = [
  'Search local guides by destination & style',
  'Manage bookings and itineraries',
  'Chat directly with your guide',
  'Secure in-app payments',
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] as const },
  },
};

function PhoneDeviceFrame({ src, alt }: { src: string; alt: string }) {
  return (
    <Box
      sx={{
        width: { xs: 132, sm: 172, md: 224 },
        aspectRatio: '390 / 844',
        flexShrink: 0,
        borderRadius: { xs: '28px', md: '38px' },
        border: { xs: '7px solid', md: '9px solid' },
        borderColor: '#0F172A',
        bgcolor: '#0F172A',
        overflow: 'hidden',
        boxShadow: '0 28px 56px rgba(15, 23, 42, 0.2), 0 8px 16px rgba(15, 23, 42, 0.1)',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        width={1179}
        height={2556}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top center',
          display: 'block',
        }}
      />
    </Box>
  );
}

export function MobileAppSection() {
  const { label: liveCitiesLabel } = useLiveCities();

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component={motion.div}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'primary.main',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                Mobile App Coming Soon
              </Typography>

              <Typography
                component="h2"
                sx={{
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  mb: 2,
                }}
              >
                Take {brand.uiName}
                <br />
                Everywhere You Go
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  color: 'text.secondary',
                  lineHeight: 1.6,
                  mb: 4,
                  maxWidth: 440,
                }}
              >
                Search guides in {liveCitiesLabel || 'live cities'}, manage bookings, and chat with locals — all
                from your pocket.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 4 }}>
                {features.map((f) => (
                  <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: 'success.light',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.9375rem',
                        color: 'text.primary',
                        fontWeight: 500,
                      }}
                    >
                      {f}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Chip
                icon={<SmartphoneOutlinedIcon sx={{ fontSize: '18px !important' }} />}
                label="Coming Soon on App Store & Google Play"
                sx={{
                  height: 44,
                  px: 0.5,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  bgcolor: '#F1F5F9',
                  color: '#475569',
                  border: '1px solid #E2E8F0',
                  cursor: 'default',
                  '& .MuiChip-icon': { color: '#64748B' },
                  '&:hover': { bgcolor: '#F1F5F9' },
                }}
              />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: { xs: 1, sm: 1.5, md: 2.5 },
                position: 'relative',
                minHeight: { xs: 280, sm: 360, md: 460 },
                pt: 2,
                pb: { xs: 0, md: 2 },
                overflow: 'hidden',
                mx: { xs: -1, sm: 0 },
              }}
            >
              {phoneScreens.map((screen, i) => (
                <Box
                  key={screen.src}
                  component={motion.div}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.18, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  sx={{
                    transform: `rotate(${screen.rotate})`,
                    mt: screen.offsetY,
                    flexShrink: 0,
                  }}
                >
                  <PhoneDeviceFrame src={screen.src} alt={screen.alt} />
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
